<template>
  <span class="mt-1 flex items-center border">
    <NumberInput
      v-model="localValue"
      class="w-full border-0 focus:ring-0"
      input-id="currency"
      :label="label"
      :has-border="false"
      @blur="commitValue"
      @keydown.enter="commitValue"
    >
      <template #suffix>
        <span class="px-2"
          >{{ currency }}<span aria-hidden="true">*</span></span
        >
      </template>
    </NumberInput>
  </span>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import NumberInput from '~/components/ui/forms/inputs/NumberInput.vue'
import { useCurrentShop } from '#storefront/composables'

const props = defineProps<{
  modelValue: number
  min: number
  max: number
  label: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

const currentShop = useCurrentShop()

const localValue = ref(props.modelValue)

watch(
  () => props.modelValue,
  (newVal) => {
    if (localValue.value !== newVal) {
      localValue.value = newVal
    }
  },
)

watch(localValue, (newVal, oldVal) => {
  if (newVal !== oldVal && newVal <= props.max && newVal >= props.min) {
    commitValue()
  }
})

const commitValue = () => {
  // If value is empty, zero, or not a number
  // treat it as invalid and reset it
  if (!localValue.value || isNaN(localValue.value)) {
    localValue.value = props.modelValue
  }

  if (localValue.value > props.max) {
    localValue.value = props.max
  }

  if (localValue.value < props.min) {
    localValue.value = props.min
  }

  emit('update:modelValue', localValue.value)
}

const currency = computed(() => {
  const formatter = new Intl.NumberFormat(currentShop.value.locale, {
    style: 'currency',
    currency: currentShop.value.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
  return formatter.format(0).replace(/\d/g, '').trim()
})
</script>
